/**
 * useTasksModuleSettings - Fonte única das configurações do módulo de Tarefas
 *
 * Expõe as flags do tenant que ligam/desligam os submódulos que reaproveitam
 * a base de Tarefas (task_type_enum):
 *   - Retenção (task_type = retention)
 *   - Jurídico (task_type = legal)
 *
 * Ambos vêm desligados por padrão ao contratar o módulo de Tarefas; o tenant
 * habilita em Configurações › Tarefas. Espelha o padrão de useVehiclesModule.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-21 v1.0.0 - Criação inicial (Retenção/Jurídico por tenant)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('useTasksModuleSettings');

/** Payload da RPC get_tasks_module_settings */
interface TasksModuleSettings {
  /** Módulo de Tarefas contratado/ativo no tenant */
  tasks_enabled: boolean;
  /** Submódulo Retenção habilitado */
  retention_enabled: boolean;
  /** Submódulo Jurídico habilitado */
  legal_enabled: boolean;
}

/**
 * Hook de configurações do módulo de Tarefas.
 * Usado pela aba de Configurações e pelos gates de UI (menus/permissões).
 */
export function useTasksModuleSettings() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  const query = useQuery({
    queryKey: ['tasks-module-settings', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async (): Promise<TasksModuleSettings> => {
      const { data, error } = await (supabase.rpc as any)('get_tasks_module_settings', {
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao carregar configurações de tarefas', { error: error.message });
        throw error;
      }
      const payload = (data || {}) as Partial<TasksModuleSettings>;
      return {
        tasks_enabled: payload.tasks_enabled ?? false,
        retention_enabled: payload.retention_enabled ?? false,
        legal_enabled: payload.legal_enabled ?? false,
      };
    },
  });

  /** Invalida o cache das configurações do módulo */
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['tasks-module-settings', tenantId] });
  };

  /**
   * Liga/desliga os submódulos (somente admin — validado na RPC).
   * Campos não informados preservam o valor atual (alteração cirúrgica).
   */
  const updateSettings = useMutation({
    mutationFn: async (input: { retentionEnabled?: boolean; legalEnabled?: boolean }) => {
      const { error } = await (supabase.rpc as any)('update_tasks_module_settings', {
        p_retention_enabled: input.retentionEnabled ?? null,
        p_legal_enabled: input.legalEnabled ?? null,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Configurações atualizadas');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return {
    tasksEnabled: query.data?.tasks_enabled ?? false,
    retentionEnabled: query.data?.retention_enabled ?? false,
    legalEnabled: query.data?.legal_enabled ?? false,
    isLoading: query.isLoading,
    updateSettings,
  };
}

/** Atalho somente-leitura para gates de UI (menu lateral, permissões) */
export function useTaskSubmodulesEnabled(): { retention: boolean; legal: boolean } {
  const { retentionEnabled, legalEnabled } = useTasksModuleSettings();
  return { retention: retentionEnabled, legal: legalEnabled };
}
